import { utc } from "PFCore/helpers/date";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useMemo } from "react";

import { CALENDAR_MONTHS_LIMIT } from "../overview/booking_module_calendar/booking_calendar/constants";
import { useMaxPastFetchesCount } from "./use_max_past_fetches_count";

interface CalendarGridScope {
  min: string;
  max: string;
}

type UseCalendarGridScope = {
  pastFetchesCount: number;
};

export const useCalendarGridScope = ({ pastFetchesCount }: UseCalendarGridScope): CalendarGridScope => {
  const {
    data: { availability }
  } = useMyAccountInfo();
  const { maxPastFetchesCount, rangeMonthsDuration, pastMonthsVisibilityLimit } = useMaxPastFetchesCount();
  const { formatDatabaseDateTime } = useDateFormatter();
  const futureMonthsLimit = availability?.future_months_visibility_limit || CALENDAR_MONTHS_LIMIT;

  const pastMonthsLimit = useMemo(() => {
    if (pastMonthsVisibilityLimit < rangeMonthsDuration) {
      return pastMonthsVisibilityLimit;
    }

    const monthsLimit = Math.min(pastFetchesCount, maxPastFetchesCount) * rangeMonthsDuration;

    if (monthsLimit === 0) {
      return rangeMonthsDuration;
    }

    return monthsLimit > pastMonthsVisibilityLimit ? pastMonthsVisibilityLimit : monthsLimit;
  }, [pastFetchesCount, rangeMonthsDuration, maxPastFetchesCount, pastMonthsVisibilityLimit]);

  const min = useMemo(
    () => formatDatabaseDateTime(utc().startOf("day").subtract(pastMonthsLimit, "months")),
    [pastMonthsLimit, rangeMonthsDuration]
  );
  const max = useMemo(
    () => formatDatabaseDateTime(utc().startOf("day").add(futureMonthsLimit, "months")),
    [futureMonthsLimit]
  );

  return {
    min,
    max
  };
};
